import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label } from '@monksflow/monks-ui';
import {
  IconPicker,
  starterCategoryIconName,
  useCreateTaxonomyCategory,
  useUpdateTaxonomyCategory,
  type TaxonomyCategory,
} from '@/entities/taxonomy-category';
import { FormSheet } from '@/shared/ui/FormSheet';

export interface CategoryModalProps {
  open: boolean;
  /** Existing category to edit; null creates a new one. */
  category: TaxonomyCategory | null;
  /**
   * Where a NEW category lands in the picker rail.
   *
   * Passed in rather than computed here: the modal holds no category list, and
   * the parent already has one. Sent only on create — on update the backend
   * treats an absent `sortOrder` as "leave it alone", which is what a rename
   * should do.
   */
  nextSortOrder: number;
  onClose: () => void;
}

/**
 * Create or edit a field category — the single place categories are defined.
 *
 * Renaming here re-points every field in the category server-side, in one
 * transaction, so the rename can't split one group into two.
 */
export function CategoryModal({ open, category, nextSortOrder, onClose }: CategoryModalProps) {
  const { t } = useTranslation('knowledge');
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    // A starter category may have no icon of its own and still render a proper
    // one (see `categoryIconFor`). Seed the picker with what's on screen, or
    // saving an unrelated edit would silently pin it to the generic default.
    setIcon(category?.icon ?? starterCategoryIconName(category?.name) ?? null);
  }, [open, category]);

  const create = useCreateTaxonomyCategory();
  // Hook order can't be conditional, so this is constructed either way and
  // simply unused when creating.
  const update = useUpdateTaxonomyCategory(category?.id ?? '');

  const trimmed = name.trim();
  const pending = create.isPending || update.isPending;
  const canSave = trimmed.length > 0 && !pending;

  const handleSave = () => {
    if (!canSave) return;
    if (category) {
      update.mutate({ name: trimmed, icon }, { onSuccess: onClose });
      return;
    }
    // Without a position every hand-made category was created at 0, tying with
    // the first shipped one and sorting above the other four — in the Add-field
    // picker rail as well as this table, with no UI able to change it.
    // TEMPORARY, for the moderated tester run: same reasoning as the field-template
    // modal — no scope control here, so a new category would be org-wide. The edit
    // path returns above, so this cannot re-scope an existing one. Drop
    // `workspaceScoped` when the run is over.
    create.mutate(
      { name: trimmed, icon, sortOrder: nextSortOrder, workspaceScoped: true },
      { onSuccess: onClose },
    );
  };

  const renaming = !!category && trimmed !== category.name && category.fieldCount > 0;

  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={
        category
          ? t('taxonomy.categoryModal.editCategory')
          : t('taxonomy.categoryModal.newCategory')
      }
    >
      <FormSheet.Body className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>{t('taxonomy.categoryModal.nameIcon')}</Label>
          <div className="flex items-center gap-2">
            <IconPicker
              value={icon}
              onChange={setIcon}
              ariaLabel={t('taxonomy.categoryModal.chooseCategoryIcon')}
            />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('taxonomy.categoryModal.eGGeography')}
              maxLength={60}
              className="bg-input"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
              autoFocus
            />
          </div>
          <span className="text-xs text-muted-foreground">
            {t('taxonomy.categoryModal.iconAppearsBesideCategoryAdd')}
          </span>
        </div>

        {/* Renaming is the one action here with reach beyond this record, so it
            says so before the user commits rather than after. */}
        {renaming && (
          <p className="rounded-lg border border-border-light bg-muted/40 p-3 text-xs text-muted-foreground">
            {t('taxonomy.categoryModal.fieldsWillMove', {
              count: category.fieldCount,
              from: category.name,
              to: trimmed,
            })}
          </p>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: category
            ? t('taxonomy.categoryModal.saveCategory')
            : t('taxonomy.categoryModal.createCategory'),
          onClick: handleSave,
          disabled: !canSave,
          loading: pending,
        }}
        secondary={{ label: t('taxonomy.categoryModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

CategoryModal.displayName = 'CategoryModal';
